import type {
  ModelDocument,
  ModelType,
  Dialect,
  DomainObject,
  Diagnostic,
  LossReport,
  NamingRule,
  Operation,
  TypeSpec,
  Representation,
} from "../shared/contracts.js";
import {
  clone,
  uid,
  createObject,
  DomainError,
  syncDocument,
  validateModel,
  getKeys,
  fieldKinds,
  entityKinds,
} from "./model.js";
import { allowedDerivationTargets, derivationSourceType, logicalPerspective } from "./derivationPolicy.js";
import { orderedBusinessKeys } from "./keyEditing.js";
export interface TransformOptions {
  logicalPrimaryKeyPolicy?: "business-first" | "retain-source";
  removeSurrogates?: boolean;
  confirmedSurrogateIds?: string[];
  sourceRepresentationId?: string;
  keyStrategy?: "business-primary" | "surrogate-primary";
  surrogateName?: string;
  fieldTypes?: Record<string, TypeSpec>;
  primaryKeys?: Record<string, string>;
  namingRule?: NamingRule;
}
export function applyName(
  name: string,
  rule: NamingRule,
  role: "table" | "column" | "surrogate" | "business" | "other" = "other",
): string {
  const prefix =
    role === "table"
      ? rule.tablePrefix
      : ["column", "surrogate", "business"].includes(role)
        ? rule.columnPrefix
        : "";
  const suffix =
    role === "surrogate"
      ? rule.surrogateSuffix
      : role === "business"
        ? rule.businessSuffix
        : "";
  let stem =
    prefix && name.startsWith(prefix) ? name.slice(prefix.length) : name;
  if (suffix && stem.endsWith(suffix)) stem = stem.slice(0, -suffix.length);
  const words = stem
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .map((w) => rule.abbreviations[w.toLowerCase()] ?? w);
  const lower = words.map((w) => w.toLowerCase()),
    capital = (s: string) =>
      s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  let result = stem;
  switch (rule.case) {
    case "snake_case":
      result = lower.join("_");
      break;
    case "UPPER_SNAKE":
      result = lower.join("_").toUpperCase();
      break;
    case "camelCase":
      result = (lower[0] ?? "") + lower.slice(1).map(capital).join("");
      break;
    case "PascalCase":
      result = lower.map(capital).join("");
      break;
    case "Title Case":
      result = lower.map(capital).join(" ");
      break;
  }
  result = prefix + result + suffix;
  return result;
}
export function previewNaming(
  document: ModelDocument,
  rule: NamingRule,
): { operations: Operation[]; diagnostics: Diagnostic[] } {
  const diagnostics: Diagnostic[] = [],
    operations: Operation[] = [];
  if (
    !rule ||
    !Number.isSafeInteger(rule.version) ||
    rule.version < 1 ||
    rule.maxLength < 1
  )
    return {
      operations,
      diagnostics: [
        {
          code: "NAMING_RULE_INVALID",
          severity: "error",
          message: "Rule version and maximum length must be positive integers.",
        },
      ],
    };
  const surrogateMembers = new Set(
    document.objects
      .filter((o) => o.kind === "key" && o.keyRole === "surrogate")
      .flatMap((k) => k.members ?? []),
  );
  const businessMembers = new Set(
    document.objects
      .filter(
        (o) =>
          o.kind === "key" &&
          ["business", "candidate"].includes(o.keyRole ?? ""),
      )
      .flatMap((k) => k.members ?? []),
  );
  const proposed = new Map<string, string>();
  for (const object of document.objects) {
    if (
      document.representations.find(
        (r) => r.representationId === object.representationId,
      )?.baseline
    )
      continue;
    const role = surrogateMembers.has(object.objectId)
      ? "surrogate"
      : businessMembers.has(object.objectId)
        ? "business"
        : entityKinds.has(object.kind)
          ? "table"
          : fieldKinds.has(object.kind)
            ? "column"
            : "other";
    const name = applyName(object.name, rule, role);
    if (!name.trim())
      diagnostics.push({
        code: "NAMING_EMPTY",
        severity: "error",
        message:
          "The naming policy removes all valid name characters. Choose a manual name or a preserve policy.",
        objectId: object.objectId,
      });
    if (name.length > rule.maxLength)
      diagnostics.push({
        code: "NAMING_LENGTH",
        severity: "error",
        message: `Proposed '${name}' exceeds ${rule.maxLength} characters. Choose an abbreviation; names are never silently truncated.`,
        objectId: object.objectId,
      });
    const ns = `${object.representationId}:${object.ownerObjectId ?? object.schema ?? ""}:${entityKinds.has(object.kind) ? "entity" : fieldKinds.has(object.kind) ? "field" : object.kind}:${name.toLowerCase()}`;
    if (proposed.has(ns))
      diagnostics.push({
        code: "NAMING_COLLISION",
        severity: "error",
        message: `Proposed name '${name}' collides in its namespace.`,
        objectId: object.objectId,
      });
    proposed.set(ns, object.objectId);
    if (name !== object.name)
      operations.push({
        type: "object.update",
        objectId: object.objectId,
        changes: { name },
      });
  }
  return { operations, diagnostics };
}
const mapType = (
  logical: string | undefined,
  dialect: Dialect,
): TypeSpec | undefined => {
  const type = logical?.toLowerCase();
  if (!type) return undefined;
  const uuidType: Record<Dialect, TypeSpec> = {
    postgresql: { name: "UUID" },
    oracle: { name: "VARCHAR2", length: 36 },
    sqlserver: { name: "UNIQUEIDENTIFIER" },
    mysql: { name: "CHAR", length: 36 },
    ase: { name: "VARCHAR", length: 36 },
  };
  if (["id", "identifier", "uuid"].includes(type)) return uuidType[dialect];
  if (["text", "string", "code"].includes(type))
    return {
      name:
        dialect === "oracle"
          ? "VARCHAR2"
          : dialect === "sqlserver"
            ? "NVARCHAR"
            : "VARCHAR",
      length: 255,
    };
  if (["integer", "int", "bounded integer"].includes(type))
    return {
      name: dialect === "oracle" ? "NUMBER" : "INTEGER",
      ...(dialect === "oracle" ? { precision: 10, scale: 0 } : {}),
    };
  if (["decimal", "exact decimal", "number"].includes(type))
    return {
      name: dialect === "oracle" ? "NUMBER" : "DECIMAL",
      precision: 18,
      scale: 2,
    };
  if (["boolean", "bool"].includes(type))
    return {
      name:
        dialect === "postgresql"
          ? "BOOLEAN"
          : dialect === "oracle"
            ? "NUMBER"
            : dialect === "mysql"
              ? "BOOLEAN"
              : "BIT",
      ...(dialect === "oracle" ? { precision: 1, scale: 0 } : {}),
    };
  if (type === "date") return { name: "DATE" };
  if (["instant", "timestamp"].includes(type))
    return {
      name:
        dialect === "oracle"
          ? "TIMESTAMP WITH TIME ZONE"
          : dialect === "postgresql"
            ? "TIMESTAMPTZ"
            : dialect === "sqlserver"
              ? "DATETIME2"
              : "DATETIME",
    };
  return undefined;
};
function translatePhysicalType(
  type: TypeSpec,
  dialect: Dialect,
): TypeSpec | undefined {
  const name = type.name.toUpperCase().replace(/\s+/g, " ").trim();
  if (["UUID", "UNIQUEIDENTIFIER"].includes(name))
    return mapType("identifier", dialect);
  if (["TEXT", "CLOB", "NCLOB", "LONGTEXT"].includes(name))
    return dialect === "sqlserver"
      ? { name: "NVARCHAR", raw: "NVARCHAR(MAX)" }
      : {
          name:
            dialect === "oracle"
              ? "CLOB"
              : dialect === "mysql"
                ? "LONGTEXT"
                : "TEXT",
        };
  if (
    [
      "VARCHAR",
      "VARCHAR2",
      "NVARCHAR",
      "NVARCHAR2",
      "CHARACTER VARYING",
    ].includes(name)
  )
    return type.length
      ? {
          name:
            dialect === "oracle"
              ? "VARCHAR2"
              : dialect === "sqlserver"
                ? "NVARCHAR"
                : "VARCHAR",
          length: type.length,
        }
      : undefined;
  if (["CHAR", "NCHAR"].includes(name))
    return { name: "CHAR", length: type.length ?? 1 };
  if (["SMALLINT", "INTEGER", "INT", "BIGINT"].includes(name))
    return dialect === "oracle"
      ? {
          name: "NUMBER",
          precision: name === "BIGINT" ? 19 : name === "SMALLINT" ? 5 : 10,
          scale: 0,
        }
      : { name: name === "INT" ? "INTEGER" : name };
  if (["NUMERIC", "DECIMAL", "NUMBER"].includes(name))
    return {
      name: dialect === "oracle" ? "NUMBER" : "DECIMAL",
      precision: type.precision ?? 38,
      scale: type.scale ?? 0,
    };
  if (["BOOLEAN", "BOOL", "BIT"].includes(name))
    return mapType("boolean", dialect);
  if (name === "DATE") return { name: "DATE" };
  if (
    [
      "TIMESTAMP",
      "TIMESTAMP WITHOUT TIME ZONE",
      "DATETIME",
      "DATETIME2",
    ].includes(name)
  )
    return {
      name:
        dialect === "sqlserver"
          ? "DATETIME2"
          : dialect === "mysql" || dialect === "ase"
            ? "DATETIME"
            : "TIMESTAMP",
    };
  if (
    ["TIMESTAMPTZ", "TIMESTAMP WITH TIME ZONE", "DATETIMEOFFSET"].includes(name)
  )
    return dialect === "mysql" || dialect === "ase"
      ? undefined
      : {
          name:
            dialect === "sqlserver"
              ? "DATETIMEOFFSET"
              : dialect === "oracle"
                ? "TIMESTAMP WITH TIME ZONE"
                : "TIMESTAMPTZ",
        };
  return undefined;
}
export function transformModel(
  document: ModelDocument,
  targetType: ModelType,
  dialect: Dialect = "postgresql",
  options: TransformOptions = {},
): {
  document: ModelDocument;
  diagnostics: Diagnostic[];
  lossReport: LossReport;
} {
  const doc = clone(document),
    diagnostics: Diagnostic[] = [],
    lossReport: LossReport = {
      preserved: [
        "Source representation and stable source object identities",
        "Business definitions and ordered candidate identifiers",
      ],
      translated: [],
      preservedOpaque: [],
      lost: [],
      unresolved: [],
    };
  if (doc.model.family !== "relational")
    return {
      document: doc,
      diagnostics: [
        {
          code: "TRANSFORMATION_FAMILY",
          severity: "error",
          message:
            "Conceptual/logical/physical transformations apply to relational models. Cross-family mappings require a separate reviewed design.",
        },
      ],
      lossReport,
    };
  let source = doc.representations.find(
    (r) =>
      r.representationId ===
      (options.sourceRepresentationId ?? doc.model.activeRepresentation),
  );
  if (!source)
    throw new DomainError("NOT_FOUND", "Source representation does not exist.");
  if (!allowedDerivationTargets(doc, source.representationId).includes(targetType)) return {
    document: doc, diagnostics: [{code:"INVALID_DERIVATION_TARGET",severity:"error",message:`${derivationSourceType(doc,source.representationId)} cannot derive directly to ${targetType}. Physical abstraction must proceed through Logical; conceptual realization must proceed through Logical or Logical-Physical.`}], lossReport,
  };
  // The combined model's conceptual stage always uses its logical perspective.
  if (targetType === "conceptual" && source.kind !== "logical") {
    source = logicalPerspective(doc,source.representationId) ?? source;
    if (source.kind !== "logical") return { document:doc, diagnostics:[{code:"LOGICAL_PERSPECTIVE_REQUIRED",severity:"error",message:"Create the combined model's logical perspective before deriving conceptual meaning."}],lossReport };
  }
  // A conceptual-to-combined derivation creates both explicit stages.
  if (targetType === "logical-physical" && source.kind === "conceptual") {
    const logical = transformModel(doc,"logical",dialect,{...options,sourceRepresentationId:source.representationId,removeSurrogates:false});
    if (logical.diagnostics.some(d=>d.severity === "error")) return logical;
    const logicalId = (sourceId: string) => logical.document.mappings.find(mapping=>mapping.sourceRefs.some(ref=>ref.objectId===sourceId && ref.representationId===source!.representationId) && mapping.destinationRefs.some(ref=>ref.representationId===logical.document.model.activeRepresentation))?.destinationRefs.find(ref=>ref.representationId===logical.document.model.activeRepresentation)?.objectId;
    const fieldTypes = Object.fromEntries(Object.entries(options.fieldTypes ?? {}).flatMap(([id,type])=>logicalId(id) ? [[logicalId(id)!,type]] : []));
    const physical = transformModel(logical.document,"logical-physical",dialect,{...options,fieldTypes,sourceRepresentationId:logical.document.model.activeRepresentation,removeSurrogates:false});
    physical.diagnostics.unshift(...logical.diagnostics);
    return physical;
  }
  // Physical-to-combined keeps the physical source and derives its logical perspective.
  if (targetType === "logical-physical" && source.kind === "physical") dialect = source.targetProfile ?? document.model.targetProfile ?? dialect;
  const targetKind = targetType === "logical-physical" ? source.kind === "physical" ? "logical" : "physical" : targetType;
  const sourceObjects = doc.objects.filter(
    (o) => o.representationId === source.representationId,
  );
  // A separately identified, frozen copy preserves the originally agreed conceptual view.
  if (source.kind === "conceptual" && !doc.model.originalConceptualBaseline) {
    const baselineId = uid(),
      idMap = new Map(sourceObjects.map((o) => [o.objectId, uid()]));
    const baseline: Representation = {
      ...clone(source),
      representationId: baselineId,
      name: "Original conceptual baseline",
      baseline: true,
      objectIds: [...idMap.values()],
      createdAt: new Date().toISOString(),
    };
    doc.representations.push(baseline);
    for (const o of sourceObjects) {
      const copy = clone(o);
      copy.objectId = idMap.get(o.objectId)!;
      copy.representationId = baselineId;
      if (copy.ownerObjectId)
        copy.ownerObjectId = idMap.get(copy.ownerObjectId);
      if (copy.members) copy.members = copy.members.map((id) => idMap.get(id)!);
      doc.objects.push(copy);
    }
    for (const r of document.relationships.filter(
      (r) => r.representationId === source.representationId,
    ))
      doc.relationships.push({
        ...clone(r),
        relationshipId: uid(),
        representationId: baselineId,
        sourceObjectId: idMap.get(r.sourceObjectId)!,
        targetObjectId: idMap.get(r.targetObjectId)!,
        sourceAttributeIds: r.sourceAttributeIds?.map((id) => idMap.get(id)!),
        targetAttributeIds: r.targetAttributeIds?.map((id) => idMap.get(id)!),
      });
    const originalDiagram = document.diagrams.find(
      (d) => d.representationId === source.representationId,
    );
    doc.diagrams.push({
      diagramId: uid(),
      diagramVersion: 1,
      name: "Original conceptual baseline",
      perspective: "conceptual",
      representationId: baselineId,
      notation: originalDiagram?.notation ?? "crow-foot",
      appearances: (originalDiagram?.appearances ?? []).map((a) => ({
        ...clone(a),
        appearanceId: uid(),
        objectId: idMap.get(a.objectId)!,
      })),
      connectors: [],
    });
    doc.model.originalConceptualBaseline = baselineId;
  }
  const repId = uid(),
    now = new Date().toISOString(),
    rep: Representation = {
      representationId: repId,
      kind: targetKind,
      name:
        targetKind === "conceptual"
          ? "Generated conceptual"
          : targetKind === "logical"
            ? "Logical interpretation"
            : "Physical realization",
      origin: "derived",
      sourceRepresentationId: source.representationId,
      objectIds: [],
      createdAt: now,
    };
  if (targetKind === "physical") rep.targetProfile = dialect;
  doc.representations.push(rep);
  const eligible = sourceObjects.filter((o) =>
    targetKind === "conceptual"
      ? entityKinds.has(o.kind) ||
        o.kind === "attribute" ||
        o.kind === "column" ||
        o.kind === "key"
      : !["index", "sequence", "constraint"].includes(o.kind) ||
        targetKind === "physical",
  );
  const ids = new Map(eligible.map((o) => [o.objectId, uid()]));
  for (const o of eligible) {
    const copy = clone(o);
    copy.objectId = ids.get(o.objectId)!;
    copy.representationId = repId;
    if (copy.ownerObjectId) copy.ownerObjectId = ids.get(copy.ownerObjectId);
    if (copy.members)
      copy.members = copy.members
        .filter((id) => ids.has(id))
        .map((id) => ids.get(id)!);
    if (entityKinds.has(o.kind))
      copy.kind =
        targetKind === "conceptual"
          ? "concept"
          : targetKind === "physical"
            ? "table"
            : "entity";
    if (fieldKinds.has(o.kind)) {
      copy.kind = targetKind === "physical" ? "column" : "attribute";
      if (targetKind === "physical") {
        const changedDialect =
          source.kind === "physical" &&
          (source.targetProfile ?? document.model.targetProfile) !== dialect;
        if (changedDialect && copy.type && !options.fieldTypes?.[o.objectId]) {
          const originalType = clone(copy.type);
          copy.type = translatePhysicalType(copy.type, dialect);
          copy.extensions = {
            ...copy.extensions,
            sourcePhysicalType: {
              profile: source.targetProfile ?? document.model.targetProfile,
              type: originalType,
            },
          };
          if (copy.type)
            copy.extensions.engineering = {
              ...((copy.extensions.engineering as Record<string, unknown>) ??
                {}),
              typeDialect: dialect,
              originalType: clone(copy.type),
            };
          if (copy.type)
            lossReport.translated.push(
              `${o.name}: ${originalType.name} to ${copy.type.name}; review target precision and comparison semantics.`,
            );
        }
        copy.type =
          options.fieldTypes?.[o.objectId] ??
          copy.type ??
          mapType(copy.logicalType, dialect);
        if (changedDialect && copy.defaultValue) {
          copy.extensions = {
            ...copy.extensions,
            sourceDefault: {
              profile: source.targetProfile ?? document.model.targetProfile,
              expression: copy.defaultValue,
            },
          };
          lossReport.preservedOpaque.push(
            `Original default expression for ${o.name}`,
          );
          lossReport.unresolved.push(`Target default expression for ${o.name}`);
          diagnostics.push({
            code: "DEFAULT_DIALECT_REVIEW",
            severity: "warning",
            message: `Review ${o.name}'s source default expression for ${dialect}; it is preserved as source evidence and not executed as a target default.`,
            objectId: copy.objectId,
          });
          delete copy.defaultValue;
        }
        if (!copy.type) {
          diagnostics.push({
            code: "TRANSFORMATION_TYPE_GAP",
            severity: "warning",
            message: `Choose a ${dialect} datatype for ${o.name} in the gap worksheet.`,
            objectId: copy.objectId,
          });
          lossReport.unresolved.push(`Physical type for ${o.name}`);
        }
      } else {
        if (copy.type && !copy.logicalType)
          copy.logicalType = copy.type.name.toLowerCase();
        delete copy.type;
        delete copy.defaultValue;
        delete copy.generated;
      }
    }
    if (targetKind === "conceptual") {
      delete copy.logicalType;
      delete copy.schema;
      delete copy.sql;
      delete copy.expression;
      delete copy.checkExpression;
      copy.extensions = {
        ...copy.extensions,
        conceptual: {
          generated: true,
          sourceObjectId: o.objectId,
          technicalDetailHidden: true,
        },
      };
    }
    if (source.kind === "physical" && targetKind !== "physical") {
      copy.extensions = {
        ...copy.extensions,
        transformation: {
          ...(copy.extensions.transformation as Record<string, unknown> ?? {}),
          origin: "physical",
          reviewState: "unreviewed",
          classification: o.generated
            ? "implementation-detail"
            : "proposed-business-meaning",
        },
      };
      if (o.generated)
        diagnostics.push({
          code: "PHYSICAL_IDENTITY_REVIEW",
          severity: "warning",
          message: `Review '${o.name}' as a technical identifier; it is not automatically a business key.`,
          objectId: copy.objectId,
        });
    }
    if (options.namingRule && targetKind === "physical")
      copy.name = applyName(
        copy.name,
        options.namingRule,
        entityKinds.has(copy.kind)
          ? "table"
          : fieldKinds.has(copy.kind)
            ? "column"
            : "other",
      );
    doc.objects.push(copy);
    doc.mappings.push({
      mappingId: uid(),
      sourceRefs: [
        {
          modelId: doc.model.modelId,
          representationId: source.representationId,
          objectId: o.objectId,
        },
      ],
      destinationRefs: [
        {
          modelId: doc.model.modelId,
          representationId: repId,
          objectId: copy.objectId,
        },
      ],
      mappingKind: "derived",
      origin: "declared",
      reviewState: "unreviewed",
      rationale: `${source.kind} to ${targetKind}; source preserved. ${source.kind === "physical" ? "Business interpretation requires review." : "Transformation proposal requires review."}`,
    });
  }
  for (const original of sourceObjects.filter((o) => entityKinds.has(o.kind))) {
    const destination = doc.objects.find(
      (o) => o.objectId === ids.get(original.objectId),
    );
    if (!destination) continue;
    const keys = getKeys(doc, destination.objectId),
      explicit = options.primaryKeys?.[original.objectId];
    const business = orderedBusinessKeys(keys).filter(k=>k.members?.length);
    const promoteBusiness = targetType === "conceptual" || (targetType === "logical" && options.logicalPrimaryKeyPolicy !== "retain-source");
    const selectedBusiness = explicit ? business.find(key => key.objectId === ids.get(explicit)) : business[0];
    if (promoteBusiness && !explicit && selectedBusiness) {
      for (const key of keys) {
        key.selectedPrimary = key === selectedBusiness;
        // Retain the business role on the selected key. It is one shared identifier.
        if (key !== selectedBusiness && key.keyRole === "primary") {
          key.extensions = {...key.extensions, sourcePrimary: true};
          key.keyRole = (key.extensions.semanticRole === "surrogate" || (key.extensions.transformation as any)?.semanticRole === "surrogate") ? "surrogate" : "candidate";
        }
      }
      for (const member of selectedBusiness.members ?? []) { const field = doc.objects.find(o=>o.objectId===member); if(field)field.nullable=false; }
      lossReport.translated.push(`${destination.name}: promoted ordered business key ${selectedBusiness.name}; retained other identifier groups.`);
    } else if (promoteBusiness && !explicit && !selectedBusiness) {
      diagnostics.push({code:"BUSINESS_KEY_REVIEW",severity:"warning",message:`${destination.name} has no confirmed business-key group. The existing identifier is retained for review.`,objectId:destination.objectId});
      lossReport.unresolved.push(`Review business identifier for ${destination.name}`);
    }
    if (explicit) {
      const chosen = keys.find((k) => k.objectId === ids.get(explicit));
      if (!chosen)
        diagnostics.push({
          code: "PRIMARY_SELECTION_INVALID",
          severity: "error",
          message: "Selected primary identifier must belong to this entity.",
          objectId: destination.objectId,
        });
      else
        for (const key of keys) {
          key.selectedPrimary = key === chosen;
          if (key.keyRole === "primary" && key !== chosen) {
            key.extensions = {...key.extensions, sourcePrimary:true};
            key.keyRole = (key.extensions.semanticRole === "surrogate" || (key.extensions.transformation as any)?.semanticRole === "surrogate") ? "surrogate" : "candidate";
          }
          if (key === chosen) for (const member of key.members ?? []) { const field = doc.objects.find(o=>o.objectId===member); if(field)field.nullable=false; }
        }
    }
    if (targetKind === "physical") {
      const strategy =
        targetType === "logical-physical" ? "business-primary" : options.keyStrategy ?? original.physicalKeyStrategy ?? "business-primary";
      destination.physicalKeyStrategy = strategy;
      let primary = keys.find(
        (k) => k.selectedPrimary || k.keyRole === "primary",
      );
      if (strategy === "surrogate-primary") {
        for (const key of keys) {
          if (key === primary) {
            if (key.keyRole === "primary") key.keyRole = "candidate";
            key.selectedPrimary = false;
          }
          for (const member of key.members ?? []) {
            const column = doc.objects.find((c) => c.objectId === member);
            if (column && (key === primary || key.keyRole === "business"))
              column.nullable = false;
          }
        }
        const columnId = uid(),
          baseName = options.surrogateName ?? `${destination.name}_key`;
        let name = baseName,
          n = 2;
        while (
          doc.objects.some(
            (o) =>
              o.ownerObjectId === destination.objectId &&
              o.name.toLowerCase() === name.toLowerCase(),
          )
        )
          name = `${baseName}_${n++}`;
        const column: DomainObject = {
          objectId: columnId,
          representationId: repId,
          kind: "column",
          name,
          ownerObjectId: destination.objectId,
          lifecycle: "active",
          tags: [],
          classifications: [],
          sourceIdentities: [],
          extensions: {
            transformation: {
              origin: "physical-only",
              semanticRole: "surrogate",
            },
          },
          type: {
            name: dialect === "oracle" ? "NUMBER" : "BIGINT",
            ...(dialect === "oracle" ? { precision: 19, scale: 0 } : {}),
          },
          nullable: false,
          generated: true,
          description:
            "Stable technical surrogate. Business uniqueness remains separately enforced.",
        };
        doc.objects.push(column, {
          objectId: uid(),
          representationId: repId,
          kind: "key",
          name: `pk_${destination.name}`,
          ownerObjectId: destination.objectId,
          lifecycle: "active",
          tags: [],
          classifications: [],
          sourceIdentities: [],
          extensions: { semanticRole: "surrogate" },
          members: [columnId],
          keyRole: "primary",
          selectedPrimary: true,
        });
        doc.mappings.push({
          mappingId: uid(),
          sourceRefs: [],
          destinationRefs: [
            {
              modelId: doc.model.modelId,
              representationId: repId,
              objectId: columnId,
            },
          ],
          mappingKind: "physical-only",
          origin: "declared",
          reviewState: "unreviewed",
          rationale:
            "Explicit physical surrogate key strategy; no business identifier invented.",
        });
        lossReport.translated.push(
          `Added surrogate primary key for ${destination.name}; retained business identifiers as unique keys.`,
        );
      } else if (primary) {
        if (primary.keyRole === "surrogate") primary.extensions = {...primary.extensions, semanticRole:"surrogate"};
        if (primary.keyRole !== "business") primary.keyRole = "primary";
        primary.selectedPrimary = true;
        for (const member of primary.members ?? []) {
          const column = doc.objects.find((c) => c.objectId === member);
          if (column) column.nullable = false;
        }
      }
      if (!primary) {
        diagnostics.push({
          code: "BUSINESS_IDENTITY_GAP",
          severity: "warning",
          message: `${original.name} has no selected business identifier. A surrogate does not establish business uniqueness.`,
          objectId: destination.objectId,
        });
        lossReport.unresolved.push(`Business identity for ${original.name}`);
      }
    }
  }
  const synthesizeReference = (
    relationship: (typeof doc.relationships)[number],
  ): boolean => {
    if (
      relationship.sourceAttributeIds?.length &&
      relationship.targetAttributeIds?.length
    )
      return true;
    const child = doc.objects.find(
        (o) => o.objectId === relationship.sourceObjectId,
      )!,
      parent = doc.objects.find(
        (o) => o.objectId === relationship.targetObjectId,
      )!;
    const key = getKeys(doc, parent.objectId).find(
        (k) => k.keyRole === "primary" || k.selectedPrimary,
      ),
      members = (key?.members ?? []).map((id) =>
        doc.objects.find((o) => o.objectId === id),
      );
    if (!key || !members.length || members.some((m) => !m?.type)) {
      diagnostics.push({
        code: "FOREIGN_KEY_GAP",
        severity: "warning",
        message: `Resolve the primary identifier and physical member types of ${parent.name} before synthesizing '${relationship.name}'.`,
        objectId: relationship.relationshipId,
      });
      lossReport.unresolved.push(
        `Foreign-key field mapping for ${relationship.name}`,
      );
      return false;
    }
    const fields: string[] = [];
    for (const member of members) {
      const baseName = `${parent.name}_${member!.name}`;
      let name = baseName,
        number = 2;
      while (
        doc.objects.some(
          (o) =>
            o.ownerObjectId === child.objectId &&
            o.name.toLowerCase() === name.toLowerCase(),
        )
      )
        name = `${baseName}_${number++}`;
      const field = createObject("column", name, repId, child.objectId);
      field.type = clone(member!.type!);
      field.nullable = relationship.targetOptional;
      field.description = `Reference to ${parent.name}.${member!.name}; generated from a resolved identifier for review.`;
      field.extensions = {
        transformation: {
          sourceRelationshipId:
            (relationship.extensions?.transformation as any)
              ?.sourceRelationshipId ?? relationship.relationshipId,
          referencedFieldId: member!.objectId,
          generatedProposal: true,
          reviewState: "unreviewed",
        },
      };
      doc.objects.push(field);
      fields.push(field.objectId);
      doc.mappings.push({
        mappingId: uid(),
        sourceRefs: [
          {
            modelId: doc.model.modelId,
            representationId: repId,
            objectId: member!.objectId,
          },
        ],
        destinationRefs: [
          {
            modelId: doc.model.modelId,
            representationId: repId,
            objectId: field.objectId,
          },
        ],
        mappingKind: "derived",
        origin: "declared",
        reviewState: "unreviewed",
        rationale:
          "Proposed foreign-key column copied from the resolved referenced identifier; no name-based existing-field merge.",
      });
    }
    relationship.sourceAttributeIds = fields;
    relationship.targetAttributeIds = members.map((m) => m!.objectId);
    lossReport.translated.push(
      `Proposed ${fields.length} foreign-key field(s) for ${relationship.name}.`,
    );
    diagnostics.push({
      code: "FOREIGN_KEY_SYNTHESIZED",
      severity: "info",
      message: `Review the generated field mapping for ${relationship.name}.`,
      objectId: relationship.relationshipId,
    });
    return true;
  };
  for (const relationship of document.relationships.filter(
    (r) => r.representationId === source.representationId,
  )) {
    if (
      !ids.has(relationship.sourceObjectId) ||
      !ids.has(relationship.targetObjectId)
    )
      continue;
    const rel = {
      ...clone(relationship),
      relationshipId: uid(),
      representationId: repId,
      sourceObjectId: ids.get(relationship.sourceObjectId)!,
      targetObjectId: ids.get(relationship.targetObjectId)!,
      sourceAttributeIds: relationship.sourceAttributeIds
        ?.map((id) => ids.get(id)!)
        .filter(Boolean),
      targetAttributeIds: relationship.targetAttributeIds
        ?.map((id) => ids.get(id)!)
        .filter(Boolean),
      extensions: {
        ...relationship.extensions,
        transformation: {
          sourceRelationshipId: relationship.relationshipId,
          reviewState: "unreviewed",
        },
      },
    };
    if (targetKind === "conceptual") {
      delete rel.sourceAttributeIds;
      delete rel.targetAttributeIds;
      delete rel.onDelete;
      delete rel.onUpdate;
    }
    if (
      targetKind === "physical" &&
      rel.sourceCardinality === "many" &&
      rel.targetCardinality === "many"
    ) {
      const parents = [
        doc.objects.find((o) => o.objectId === rel.sourceObjectId)!,
        doc.objects.find((o) => o.objectId === rel.targetObjectId)!,
      ];
      const ready = parents.every((parent) => {
        const key = getKeys(doc, parent.objectId).find(
          (k) => k.keyRole === "primary" || k.selectedPrimary,
        );
        return (
          !!key?.members?.length &&
          key.members.every(
            (id) => doc.objects.find((o) => o.objectId === id)?.type,
          )
        );
      });
      if (ready) {
        let bridgeName = `${parents[0].name}_${parents[1].name}`,
          counter = 2;
        const base = bridgeName;
        while (
          doc.objects.some(
            (o) =>
              o.representationId === repId &&
              !o.ownerObjectId &&
              o.name.toLowerCase() === bridgeName.toLowerCase(),
          )
        )
          bridgeName = `${base}_${counter++}`;
        const bridge = createObject("table", bridgeName, repId);
        bridge.description = `Proposed associative entity for ${relationship.name}. Review its business name, grain and any relationship attributes.`;
        bridge.extensions = {
          transformation: {
            sourceRelationshipId: relationship.relationshipId,
            generatedProposal: true,
            reviewState: "unreviewed",
          },
        };
        doc.objects.push(bridge);
        const keyMembers: string[] = [];
        for (const [index, parent] of parents.entries()) {
          const fk: typeof rel = {
            ...clone(rel),
            relationshipId: uid(),
            name: `fk_${bridgeName}_${index + 1}`,
            sourceObjectId: bridge.objectId,
            targetObjectId: parent.objectId,
            sourceCardinality: "many",
            targetCardinality: "one",
            sourceOptional: true,
            targetOptional: false,
            identifying: true,
            sourceAttributeIds: undefined,
            targetAttributeIds: undefined,
          };
          synthesizeReference(fk);
          keyMembers.push(...(fk.sourceAttributeIds ?? []));
          doc.relationships.push(fk);
        }
        const key = createObject(
          "key",
          `pk_${bridgeName}`,
          repId,
          bridge.objectId,
        );
        key.keyRole = "primary";
        key.selectedPrimary = true;
        key.members = keyMembers;
        key.description =
          "Proposed uniqueness of the two referenced identities; temporal repetition requires an explicit business-key refinement.";
        doc.objects.push(key);
        doc.mappings.push({
          mappingId: uid(),
          sourceRefs: parents.map((parent) => ({
            modelId: doc.model.modelId,
            representationId: repId,
            objectId: parent.objectId,
          })),
          destinationRefs: [
            {
              modelId: doc.model.modelId,
              representationId: repId,
              objectId: bridge.objectId,
            },
          ],
          mappingKind: "derived",
          origin: "declared",
          reviewState: "unreviewed",
          rationale: `Associative entity proposal for ${relationship.name}; original many-to-many relationship remains in its source representation.`,
        });
        lossReport.translated.push(
          `Resolved ${relationship.name} through a proposed associative entity ${bridgeName}.`,
        );
        continue;
      }
      diagnostics.push({
        code: "MANY_TO_MANY_RESOLUTION",
        severity: "warning",
        message: `Resolve endpoint identifiers and types for '${relationship.name}' before creating an associative-entity proposal.`,
        objectId: rel.relationshipId,
      });
      lossReport.unresolved.push(`Associative entity for ${relationship.name}`);
    } else if (targetKind === "physical") {
      if (rel.sourceCardinality === "one" && rel.targetCardinality === "many") {
        [rel.sourceObjectId, rel.targetObjectId] = [
          rel.targetObjectId,
          rel.sourceObjectId,
        ];
        [rel.sourceAttributeIds, rel.targetAttributeIds] = [
          rel.targetAttributeIds,
          rel.sourceAttributeIds,
        ];
        [rel.sourceOptional, rel.targetOptional] = [
          rel.targetOptional,
          rel.sourceOptional,
        ];
        rel.sourceCardinality = "many";
        rel.targetCardinality = "one";
      }
      synthesizeReference(rel);
    }
    doc.relationships.push(rel);
  }
  if (targetType === "logical" && options.removeSurrogates) {
    const confirmed = new Set(options.confirmedSurrogateIds ?? []);
    const marked = new Set(sourceObjects.filter(o=>fieldKinds.has(o.kind) && (confirmed.has(o.objectId) || o.extensions.semanticRole === "surrogate" || (o.extensions.transformation as any)?.semanticRole === "surrogate")).map(o=>o.objectId));
    for (const key of sourceObjects.filter(o=>o.kind === "key" && (o.keyRole === "surrogate" || o.extensions.semanticRole === "surrogate" || (o.extensions.transformation as any)?.semanticRole === "surrogate"))) for(const id of key.members ?? []) marked.add(id);
    if (!marked.size) diagnostics.push({code:"SURROGATE_CONFIRMATION_REQUIRED",severity:"warning",message:"No explicit surrogate metadata or confirmed attributes were found. No fields were removed; names ending in ID are not evidence of surrogate status."});
    for(const sourceId of marked) {
      const id = ids.get(sourceId), field = doc.objects.find(o=>o.objectId===id);
      if(!field)continue;
      const dependencies = doc.relationships.filter(r=>r.representationId===repId && [...r.sourceAttributeIds ?? [],...r.targetAttributeIds ?? []].includes(id!));
      const keyDependencies = getKeys(doc,field.ownerObjectId!).filter(k=>k.members?.includes(id!));
      const unsafeKeys = keyDependencies.filter(k=>k.selectedPrimary || k.keyRole === "business" || k.keyRole === "unique" || (k.keyRole === "candidate" && !k.extensions.sourcePrimary));
      const expressionDependencies = doc.objects.filter(o=>o.representationId===repId && o.objectId!==id && [o.expression,o.checkExpression,o.defaultValue].some(value=>value?.includes(field.name)));
      const memberDependencies = doc.objects.filter(o=>o.representationId===repId && o.objectId!==id && o.kind !== "key" && [...o.members ?? [],...o.grain ?? []].includes(id!));
      const mappingDependencies = doc.mappings.filter(m=>m.sourceRefs.some(ref=>ref.objectId===id) || (m.destinationRefs.length > 1 && m.destinationRefs.some(ref=>ref.objectId===id)));
      if(dependencies.length || unsafeKeys.length || expressionDependencies.length || memberDependencies.length || mappingDependencies.length) {
        const detail = [...dependencies.map(r=>r.name),...unsafeKeys.map(k=>k.name),...expressionDependencies.map(o=>o.name),...memberDependencies.map(o=>o.name),...mappingDependencies.map(m=>m.mappingId)].join(', ');
        diagnostics.push({code:"SURROGATE_DEPENDENCY_REVIEW",severity:"warning",message:`Retained ${field.name}: dependent mappings or identifiers (${detail}) need explicit business-field mappings before removal. No child attributes were invented.`,objectId:id});
        lossReport.unresolved.push(`Surrogate removal for ${field.name}: ${detail}`);
        continue;
      }
      const remove = new Set([id!,...keyDependencies.map(k=>k.objectId)]);
      // Composite obsolete keys are removed together; other attributes are retained.
      doc.objects = doc.objects.filter(o=>!remove.has(o.objectId));
      doc.mappings = doc.mappings.filter(m=>!m.destinationRefs.some(ref=>remove.has(ref.objectId)));
      lossReport.translated.push(`Removed explicitly identified surrogate ${field.name} from logical proposal; source and relationship definitions preserved.`);
    }
  }
  const diagram = document.diagrams.find(
    (d) => d.representationId === source.representationId,
  );
  doc.diagrams.push({
    diagramId: uid(),
    diagramVersion: 1,
    name: rep.name,
    perspective: targetKind,
    representationId: repId,
    notation: diagram?.notation ?? "crow-foot",
    appearances: (
      diagram?.appearances ??
      sourceObjects
        .filter((o) => !o.ownerObjectId)
        .map((o, i) => ({
          appearanceId: uid(),
          objectId: o.objectId,
          position: { x: (i % 4) * 340, y: Math.floor(i / 4) * 300 },
        }))
    )
      .filter((a) => ids.has(a.objectId))
      .map((a) => ({
        ...clone(a),
        appearanceId: uid(),
        objectId: ids.get(a.objectId)!,
      })),
    connectors: [],
  });
  for (const [index, object] of doc.objects
    .filter((o) => o.representationId === repId && !o.ownerObjectId)
    .entries()) {
    const targetDiagram = doc.diagrams[doc.diagrams.length - 1];
    if (!targetDiagram.appearances.some((a) => a.objectId === object.objectId))
      targetDiagram.appearances.push({
        appearanceId: uid(),
        objectId: object.objectId,
        position: { x: (index % 4) * 340, y: Math.floor(index / 4) * 360 },
      });
  }
  doc.model.activeRepresentation = repId;
  doc.model.targetProfile = dialect;
  if (
    targetKind === "physical" &&
    doc.representations.some((r) => r.kind === "logical")
  )
    doc.model.modelType = "logical-physical";
  else if (targetType === "logical-physical")
    doc.model.modelType = "logical-physical";
  else doc.model.modelType = targetType;
  syncDocument(doc);
  diagnostics.push(...validateModel(doc));
  lossReport.translated.push(
    `Created separately identified ${targetKind} representation with object mappings.`,
  );
  return { document: doc, diagnostics, lossReport };
}
